import * as THREE from "three";

async function countObjectsInObj(file) {
    const text = await file.text();
    const lines = text.split("\n");
    const objects = new Set();

    for (const line of lines) {
        if (line.startsWith("# Object")) {
            try {
                const parts = line.substring(2).split(",");
                if (parts[0]) {
                    const objectParts = parts[0].split(":");
                    // Extract full object name (everything after "ObjectXX:")
                    // e.g., "Object28: 17: 4T-1 - Molar Tube" -> "17: 4T-1 - Molar Tube"
                    const objectName = objectParts.slice(1).join(":").trim();
                    if (objectName) {
                        objects.add(objectName);
                    }
                }
            } catch (error) {
                console.warn("Error parsing object line:", line);
            }
        }
    }

    return objects.size;
}

function createGradientTexture() {
    const canvas = document.createElement("canvas");
    canvas.width = 2;
    canvas.height = 2;
    const ctx = canvas.getContext("2d");
    const gradient = ctx.createLinearGradient(0, 0, 0, 2);
    gradient.addColorStop(0, "#000000");
    gradient.addColorStop(1, "#222222");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 2, 2);
    const texture = new THREE.CanvasTexture(canvas);
    texture.minFilter = THREE.LinearFilter;
    return texture;
}
async function loadObjFile(file, loadColors = false) {
    const text = await file.text();
    const lines = text.split("\n");
    const vertices = [];
    const faces = [];
    const colors = [];

    for (const line of lines) {
        if (line.startsWith("v ")) {
            const parts = line.split(" ").filter(Boolean);
            vertices.push(
                parseFloat(parts[1]),
                parseFloat(parts[2]),
                parseFloat(parts[3])
            );
            if (parts.length >= 7 && loadColors) {
                colors.push(
                    parseFloat(parts[4]),
                    parseFloat(parts[5]),
                    parseFloat(parts[6])
                );
            } else {
                colors.push(1, 1, 1);
            }
        } else if (line.startsWith("f ")) {
            const parts = line.split(" ").filter(Boolean);
            faces.push(
                parseInt(parts[1]) - 1,
                parseInt(parts[2]) - 1,
                parseInt(parts[3]) - 1
            );
        }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(vertices, 3)
    );
    geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    geometry.setIndex(faces);
    geometry.computeVertexNormals();

    return new THREE.Mesh(
        geometry,
        new THREE.MeshStandardMaterial({
            side: THREE.DoubleSide,
            vertexColors: true,
            metalness: 0.3,
            roughness: 0.5
        })
    );
}

async function loadObjFileWithClasses(
    file,
    removeUnlabeled = false,
    loadColors = false
) {
    const text = await file.text();
    const lines = text.split("\n");
    const vertices = [];
    const faces = [];
    const colors = [];
    const labels = new Map();
    const vertexToToothID = new Map();

    let currentObjectId = null;
    let currentObjectName = null;
    let currentToothId = -1;

    for (const line of lines) {
        if (line.startsWith("# Object")) {
            try {
                const parts = line.substring(2).split(",");
                if (!parts[0]) throw new Error("Invalid object line format");

                const objectParts = parts[0].split(":");
                if (objectParts.length < 2)
                    throw new Error("Invalid object name format");

                currentObjectId = objectParts[0].trim();
                // Extract full object name (everything after "ObjectXX:")
                // e.g., "Object28: 17: 4T-1 - Molar Tube" -> "17: 4T-1 - Molar Tube"
                currentObjectName = objectParts.slice(1).join(":").trim();

                // Parse tooth ID from object name
                if (currentObjectName.startsWith("Tooth ")) {
                    const toothNum = currentObjectName.split(" ")[1];
                    if (!toothNum) throw new Error("Invalid tooth number");
                    currentToothId = parseInt(toothNum);
                    if (isNaN(currentToothId))
                        throw new Error("Invalid tooth number");
                } else if (
                    currentObjectName.includes("Soft") ||
                    currentObjectName.includes("Gingival")
                ) {
                    currentToothId = 0;
                } else {
                    currentToothId = -1;
                }
            } catch (error) {
                console.warn(`Error parsing object line: ${error.message}`);
                currentObjectId = null;
                currentObjectName = null;
                currentToothId = -1;
            }
        } else if (line.startsWith("v ")) {
            // Skip vertices with tooth ID -1 if removeUnlabeled is true
            if (removeUnlabeled && currentToothId === -1) {
                continue;
            }

            const parts = line.split(" ").filter(Boolean);
            vertices.push(
                parseFloat(parts[1]),
                parseFloat(parts[2]),
                parseFloat(parts[3])
            );
            if (parts.length >= 7 && loadColors) {
                colors.push(
                    parseFloat(parts[4]),
                    parseFloat(parts[5]),
                    parseFloat(parts[6])
                );
            } else {
                colors.push(1, 1, 1);
            }
            if (currentObjectName) {
                labels.set(vertices.length / 3 - 1, currentObjectName);
                vertexToToothID.set(vertices.length / 3 - 1, currentToothId);
            }
        } else if (line.startsWith("f ")) {
            const parts = line.split(" ").filter(Boolean);
            const v1 = parseInt(parts[1]) - 1;
            const v2 = parseInt(parts[2]) - 1;
            const v3 = parseInt(parts[3]) - 1;

            // Only add face if all vertices exist (not filtered out)
            if (
                !removeUnlabeled ||
                (vertexToToothID.get(v1) !== -1 &&
                    vertexToToothID.get(v2) !== -1 &&
                    vertexToToothID.get(v3) !== -1)
            ) {
                faces.push(v1, v2, v3);
            }
        }
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(vertices, 3)
    );
    geometry.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    geometry.setIndex(faces);
    geometry.computeVertexNormals();

    const mesh = new THREE.Mesh(
        geometry,
        new THREE.MeshStandardMaterial({
            side: THREE.DoubleSide,
            vertexColors: true,
            metalness: 0.3,
            roughness: 0.5
        })
    );

    return {
        object: mesh,
        vertexToToothID: vertexToToothID
    };
}

async function loadStlFile(file) {
    const contents = await file.arrayBuffer();
    const stlData = new Uint8Array(contents);
    const isBinary = !isAsciiStl(stlData);

    let positions = [];
    let indices = [];

    if (isBinary) {
        parseBinaryStl(stlData, positions, indices);
    } else {
        parseAsciiStl(new TextDecoder().decode(stlData), positions, indices);
    }

    // Create a new geometry with unique vertices
    const uniqueVertices = new Map();
    const newPositions = [];
    const newIndices = [];

    // Process each vertex
    for (let i = 0; i < indices.length; i++) {
        const index = indices[i];
        const x = positions[index * 3];
        const y = positions[index * 3 + 1];
        const z = positions[index * 3 + 2];
        // Use float values directly as key by multiplying by a large number to preserve precision
        const vertexKey = x * 1e6 + y * 1e3 + z;

        if (!uniqueVertices.has(vertexKey)) {
            const newIndex = newPositions.length / 3;
            uniqueVertices.set(vertexKey, newIndex);
            newPositions.push(x, y, z);
        }
        newIndices.push(uniqueVertices.get(vertexKey));
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
        "position",
        new THREE.Float32BufferAttribute(newPositions, 3)
    );
    geometry.setIndex(newIndices);
    geometry.computeVertexNormals();

    return new THREE.Mesh(
        geometry,
        new THREE.MeshStandardMaterial({
            side: THREE.DoubleSide,
            vertexColors: true,
            metalness: 0.3,
            roughness: 0.5
        })
    );
}

function isAsciiStl(data) {
    const ascii = new TextDecoder().decode(data.slice(0, 5));
    return ascii.toLowerCase().startsWith("solid");
}

function parseBinaryStl(data, positions, indices) {
    const dataView = new DataView(data.buffer);
    const triangles = dataView.getUint32(80, true);
    let offset = 84;

    for (let i = 0; i < triangles; i++) {
        // Skip normal
        offset += 12;

        // Read vertices
        for (let j = 0; j < 3; j++) {
            const x = dataView.getFloat32(offset, true);
            const y = dataView.getFloat32(offset + 4, true);
            const z = dataView.getFloat32(offset + 8, true);
            positions.push(x, y, z);
            indices.push(i * 3 + j);
            offset += 12;
        }

        // Skip attribute byte count
        offset += 2;
    }
}

function parseAsciiStl(data, positions, indices) {
    const lines = data.split("\n");
    let vertexIndex = 0;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line.startsWith("vertex")) {
            const parts = line.split(/\s+/);
            positions.push(
                parseFloat(parts[1]),
                parseFloat(parts[2]),
                parseFloat(parts[3])
            );
            indices.push(vertexIndex++);
        }
    }
}

function updateMeshVisualization(state) {
    if (!state.mesh) return;

    // Remove existing visualizations
    state.scene.remove(state.scene.getObjectByName("wireframe"));
    state.scene.remove(state.scene.getObjectByName("pointcloud"));

    if (state.showWireframe) {
        const wireframe = new THREE.LineSegments(
            new THREE.WireframeGeometry(state.mesh.geometry),
            state.wireframeMaterial
        );
        wireframe.name = "wireframe";
        state.scene.add(wireframe);
    }

    if (state.showPointCloud) {
        const pointCloud = new THREE.Points(
            state.mesh.geometry,
            state.pointsMaterial
        );
        pointCloud.name = "pointcloud";
        state.scene.add(pointCloud);
    }

    state.mesh.visible = !state.showPointCloud;
}

export {
    createGradientTexture,
    loadObjFile,
    loadObjFileWithClasses,
    loadStlFile,
    isAsciiStl,
    updateMeshVisualization,
    countObjectsInObj
};
